import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PencilIcon, PlusIcon } from "@/components/developer/DashboardIcons";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ProjectForm from "./projects/ProjectForm";
import ProjectList from "./projects/ProjectList";

const Projects = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingProject, setEditingProject] = useState<any>(null);
  const { toast } = useToast();

  const { data: projects, isFetching: isFetchingProjects, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false });

      if (error) {
        toast({
          title: "Error",
          description: "Failed to fetch projects data",
          variant: "destructive",
        });
        throw error;
      }

      return data;
    }
  });

  const { data: skillTerms = [] } = useQuery({
    queryKey: ['skillTerms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('skill_terms')
        .select('*')
        .order('term');
      
      if (error) throw error;
      return data;
    },
  });

  const handleSave = async (projectData: any) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      if (editingProject) {
        // Update existing project
        const { error } = await supabase
          .from('projects')
          .update({
            title: projectData.title,
            year: projectData.year,
            description: projectData.description,
            url: projectData.url,
            technologies: projectData.technologies,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingProject.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Project updated successfully",
        });
      } else {
        // Create new project
        const { error } = await supabase
          .from('projects')
          .insert({
            user_id: user.user.id,
            title: projectData.title,
            year: projectData.year,
            description: projectData.description,
            url: projectData.url,
            technologies: projectData.technologies,
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Project created successfully",
        });
      }

      await refetch();
      setIsEditing(false);
      setEditingProject(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save project",
        variant: "destructive",
      });
      console.error('Error saving project:', error);
    }
  };

  const handleDelete = async () => {
    if (!editingProject?.id) return;

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', editingProject.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project deleted successfully",
      });

      await refetch();
      setIsEditing(false);
      setEditingProject(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive",
      });
      console.error('Error deleting project:', error);
    }
  };

  const handleEdit = (project: any) => {
    setEditingProject(project);
    setIsEditing(true);
  };

  if (isFetchingProjects) {
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Projects</h2>
        </div>
        <div className="animate-pulse space-y-6">
          <div className="h-24 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  if (isEditing) {
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Projects</h2>
        </div>
        <ProjectForm
          initialData={editingProject}
          onSave={handleSave}
          onCancel={() => {
            setIsEditing(false);
            setEditingProject(null);
          }}
          onDelete={handleDelete}
          skillTerms={skillTerms}
        />
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <h2 className="text-xl font-semibold">Projects</h2>
        <div className="flex gap-2">
          {projects && projects.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setEditingProject(null);
                setIsEditing(true);
              }}
              className="gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Add
            </Button>
          )}
        </div>
      </div>
      {(!projects || projects.length === 0) ? (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 mb-4">No projects added yet</p>
          <Button
            onClick={() => {
              setEditingProject(null);
              setIsEditing(true);
            }}
            className="gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Project
          </Button>
        </div>
      ) : (
        <ProjectList 
          projects={projects} 
          onEdit={handleEdit}
        />
      )}
    </Card>
  );
};

export default Projects;